<main class="setup-page">
  <section class="setup-stage" aria-labelledby="setup-title">
    <div class="setup-context">
      <img [ngSrc]="logoSrc" width="148" height="32" alt="Automato" priority class="setup-logo" />

      <div class="setup-copy">
        <p appBadge tone="neutral" size="sm" class="setup-kicker">{{ displayEmail() }}</p>
        <h1 id="setup-title">Vamos configurar o seu workspace</h1>
        <p>
          Vamos definir a base, preparar o acesso da equipe e deixar as primeiras integrações
          prontas para o Automato operar no contexto certo.
        </p>
      </div>

      <ol class="setup-steps" aria-label="Progresso do setup">
        @for (step of setupSteps; track step.id; let index = $index) {
          <li
            appUiPanelCard
            class="setup-step-card"
            [class.is-current]="isStepCurrent(step.id)"
            [class.is-complete]="isStepComplete(step.id)"
          >
            <span class="setup-step-index">{{ index + 1 }}</span>
            <button
              appUiOptionRow
              compact
              type="button"
              [selected]="isStepCurrent(step.id)"
              (click)="goToStep(step.id)"
              [disabled]="step.id > currentStep()"
            >
              <span class="setup-step-label">{{ step.label }}</span>
              <span class="setup-step-description">{{ step.description }}</span>
            </button>
            <span class="setup-step-state">{{ stepState(step.id) }}</span>
          </li>
        }
      </ol>
    </div>

    <form class="setup-form" [formGroup]="form" novalidate (ngSubmit)="submit()">
      <div class="setup-form__header">
        <p>
          Passo {{ currentStep() }} de {{ setupSteps.length }} · {{ currentStepMeta().section }}
        </p>
        <h2>{{ currentStepMeta().title }}</h2>
      </div>

      @switch (currentStep()) {
        @case (1) {
          <div class="setup-step-panel">
            @if (showsWorkspacePicker()) {
              <div class="setup-workspace-picker">
                <p appUiLabel>
                  Encontramos {{ discoveryWorkspaces().length === 1 ? 'um workspace' : 'workspaces' }}
                  pelo domínio do seu e-mail
                </p>
                @for (workspace of discoveryWorkspaces(); track workspace.businessId) {
                  <article appUiPanelCard class="setup-workspace-picker-item">
                    <div>
                      <strong>{{ workspace.businessName }}</strong>
                      <span class="setup-workspace-picker-domain">{{ workspace.domain }}</span>
                    </div>
                    <button
                      appButton
                      variant="primary"
                      size="sm"
                      type="button"
                      [disabled]="isJoiningWorkspace() !== null"
                      (click)="joinWorkspace(workspace)"
                    >
                      {{ isJoiningWorkspace() === workspace.businessId ? 'Entrando' : 'Entrar' }}
                    </button>
                  </article>
                }
                <p class="setup-workspace-picker-divider">ou crie um novo workspace abaixo</p>
              </div>
            }

            <label class="setup-field">
              <span appUiLabel>Nome</span>
              <input
                appInput
                type="text"
                formControlName="organizationName"
                placeholder="Digite o nome da organização"
                autocomplete="organization"
              />
            </label>

            <div class="setup-form__split">
              <div class="setup-field">
                <span appUiLabel id="team-size-label">Quantidade de pessoas</span>
                <div
                  appSelect
                  aria-labelledby="team-size-label"
                  [ngpSelectValue]="selectedTeamSize()"
                  (ngpSelectValueChange)="setTeamSize($event)"
                >
                  <span>{{ selectedTeamSize() }}</span>
                  <div *ngpSelectPortal appSelectDropdown>
                    @for (option of teamSizeOptions; track option) {
                      <div appSelectOption [ngpSelectOptionValue]="option">
                        {{ option }}
                        @if (selectedTeamSize() === option) {
                          <span aria-hidden="true">✓</span>
                        }
                      </div>
                    }
                  </div>
                </div>
              </div>

              <div class="setup-field">
                <span appUiLabel id="operating-focus-label">Foco inicial</span>
                <div
                  appSelect
                  aria-labelledby="operating-focus-label"
                  [ngpSelectValue]="selectedFocus()"
                  (ngpSelectValueChange)="setOperatingFocus($event)"
                >
                  <span>{{ selectedFocus() }}</span>
                  <div *ngpSelectPortal appSelectDropdown>
                    @for (option of focusOptions; track option) {
                      <div appSelectOption [ngpSelectOptionValue]="option">
                        {{ option }}
                        @if (selectedFocus() === option) {
                          <span aria-hidden="true">✓</span>
                        }
                      </div>
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>
        }

        @case (2) {
          <div class="setup-step-panel">
            <div class="setup-invite-row">
              <label class="setup-field setup-invite-email">
                <span appUiLabel>E-mail do convite</span>
                <input
                  appInput
                  type="email"
                  formControlName="inviteEmail"
                  placeholder="Digite o e-mail"
                  autocomplete="email"
                  (keydown.enter)="createInviteFromForm($event)"
                />
              </label>

              <div class="setup-field setup-invite-role">
                <span appUiLabel id="invite-role-label">Role</span>
                <div
                  appSelect
                  aria-labelledby="invite-role-label"
                  [invalid]="form.controls.inviteRoleId.invalid && form.controls.inviteRoleId.touched"
                  [ngpSelectValue]="selectedInviteRoleIds()[0]"
                  (ngpSelectValueChange)="setInviteRole($event)"
                >
                  <span>{{ selectedInviteRoleName() }}</span>
                  <div *ngpSelectPortal appSelectDropdown>
                    @for (role of availableRoles(); track role.id) {
                      <div appSelectOption [ngpSelectOptionValue]="role.id">
                        {{ role.name }}
                        @if (isInviteRoleSelected(role.id)) {
                          <span aria-hidden="true">✓</span>
                        }
                      </div>
                    }
                  </div>
                </div>
              </div>

              <button
                appButton
                class="setup-add-invite"
                variant="primary"
                type="button"
                aria-label="Criar convite"
                (click)="createInviteFromForm()"
                [disabled]="isSubmitting() || isCreatingInvite()"
              >
                <ng-icon name="heroPlus" size="18" aria-hidden="true" />
              </button>
            </div>

            <div class="setup-invite-list" aria-label="Convites criados">
              @for (invitation of createdInvitations(); track invitation.id) {
                <article appUiPanelCard class="setup-invite-item">
                  <span>
                    <strong>{{ invitation.email }}</strong>
                    <small>Expira em {{ invitation.expiresAt | date: 'dd/MM/yyyy' }}</small>
                  </span>
                  <em appBadge size="xs" [tone]="invitation.status === 'pending' ? 'accent' : 'neutral'">
                    {{ invitationStatusLabel(invitation.status) }}
                  </em>
                  <button
                    appButton
                    class="setup-remove-invite"
                    variant="ghost"
                    size="sm"
                    type="button"
                    aria-label="Remover convite"
                    [disabled]="invitation.status !== 'pending' || isDeletingInvite(invitation.id)"
                    (click)="revokeInvite(invitation)"
                  >
                    <ng-icon name="heroTrash" size="16" aria-hidden="true" />
                  </button>
                </article>
              } @empty {
                <p class="setup-note">
                  Os convites criados aparecerão aqui.
                </p>
              }
            </div>

            @if (canConfigureAutoInvite()) {
              <label class="setup-field">
                <span appUiLabel>Domínio para convite automático</span>
                <input
                  appInput
                  type="text"
                  formControlName="autoInviteDomain"
                  readonly
                  autocomplete="off"
                />
              </label>

              <div appUiPanelCard class="setup-check">
                <button appCheckbox type="button" formControlName="autoInviteEnabled" aria-label="Ativar convite automático por domínio">
                  <ng-icon name="heroCheck" size="13" aria-hidden="true" class="opacity-0 transition-opacity group-data-[checked]:opacity-100" />
                </button>
                <span>
                  <strong>Ativar convite automático por domínio</strong>
                  <small>Novos usuários com esse domínio entram como convidados pendentes.</small>
                </span>
              </div>
            }
          </div>
        }

        @case (3) {
          <div class="setup-step-panel">
            <div class="setup-field">
              <span appUiLabel>Integrações recomendadas para {{ selectedFocus() }}</span>
              <div class="setup-integrations" aria-label="Integrações recomendadas">
                @for (integration of recommendedIntegrations(); track integration.key) {
                  <button
                    appUiOptionRow
                    compact
                    class="setup-integration"
                    type="button"
                    [selected]="isIntegrationSelected(integration.key)"
                    (click)="toggleIntegration(integration.key)"
                  >
                    <span>
                      <strong>{{ integration.name }}</strong>
                      <small>{{ integration.detail }}</small>
                    </span>
                    <span
                      appBadge
                      size="xs"
                      class="setup-integration-status"
                      [tone]="isIntegrationSelected(integration.key) ? 'accent' : 'neutral'"
                    >
                      @if (isIntegrationSelected(integration.key)) {
                        Selecionada
                      } @else {
                        Opcional
                      }
                    </span>
                  </button>
                }
              </div>
            </div>

            <p class="setup-note">
              Esta etapa é uma prévia. As conexões serão habilitadas no próximo ciclo de integração.
            </p>
          </div>
        }
      }

      <div class="setup-actions">
        @if (currentStep() > 1) {
          <button
            appButton
            variant="secondary"
            type="button"
            (click)="previousStep()"
            [disabled]="isSubmitting()"
          >
            Voltar
          </button>
        }

        <button appButton variant="primary" type="submit" [disabled]="isSubmitting()">
          @if (isSubmitting()) {
            Salvando
          } @else {
            Continuar
          }
        </button>
      </div>
    </form>
  </section>
</main>
